import { Classes } from "@blueprintjs/core";
import { Form, Input } from "decorator-form";
import React from "react";

import InputType from "common/form/InputType";
import { ButtonView } from "common/views/button/Button.view";
import { DialogView } from "common/views/dialog/Dialog.view";
import Label from "core/components/label/Label";
import { IPanelProps, Panel } from "core/decorators/Panel";

@Form({title: "Intel Header/Footer Panel"})
class IntelHeaderFooterConfig {

	@Input({type: InputType.CHECKBOX, title: "Enable TMS Code"})
	public enableTmsCode?: boolean;

	@Input({type: InputType.CHECKBOX, title: "Enable Header Footer"})
	public enableHeaderFooter?: boolean;
	

	@Input({type: InputType.TEXT, title: "Intel header script link"})
	public headerScriptLink?: string;


	@Input({type: InputType.CHECKBOX, title: "Enable Sub Navigation"})
	public enableSubNavigation?: boolean;

	@Input({type: InputType.JSON, title: "Sub Navigation JSON"})
	public subNavJson?: any;

	@Input({type: InputType.TEXT, title: "Default Site Time Zone"})
	public defaultTimeZone?: string;
}

/**
 * - embedded intel header/footer
 * - unsupported browser dialog
 * - embedded intel tracking code ("TMS")
 */

@Panel({
	id: "intelrco-header-footer",
	configClass: IntelHeaderFooterConfig
})
class IntelHeaderFooter extends React.PureComponent<IPanelProps<IntelHeaderFooterConfig>, { modalOpen: boolean; }> {

	public constructor(props: IPanelProps<IntelHeaderFooterConfig>) {
		super(props);
		this.state = { modalOpen: false };
	}

	public render() {

		return (
			<DialogView
				viewKey="dialog"
				variant="default"
				isCloseButtonShown={true}
				title={<Label k="intelrco.support.blade.unsupported.browser.title" v="Unsupported Browser" />}
				onClose={this.closeDialog}
				isOpen={this.state.modalOpen}
			>
				<div className={Classes.DIALOG_BODY}>
					<Label
						k="intelrco.support.blade.unsupported.browser.description"
						v="Some aspects of this site may not display correctly in Internet Explorer. We recommend upgrading to a more recent browser for the best experience."
					/>
				</div>
				<div className={Classes.DIALOG_FOOTER}>
					<div className={Classes.DIALOG_FOOTER_ACTIONS}>
						<ButtonView viewKey="close" onClick={this.closeDialog}>
							<Label k="intelrco.support.blade.unsupported.browser.link" v="Continue" />
						</ButtonView>
					</div>
				</div>
			</DialogView>
		);
	}

	public componentDidMount() {
		
		this.renderHeaderFooter();
		
		const { config, language, actions } = this.props;

		// unsupported browser prompt
		const ua = window.navigator.userAgent;
		if (ua.indexOf("MSIE ") > -1 || navigator.userAgent.match(/Trident.*rv\:11\./)) {
			this.setState({modalOpen: true});
		}

		if (config.enableHeaderFooter && config.enableSubNavigation) {
			document.addEventListener("ighfBreadcrumbReady", function() {
				// create sub navigation
				// call javascript methods under headerchooser js file provided by Intel to create sub navigation menu
				var json = config.subNavJson[language];
				if (json) {
					json.breadcrumb.callback=function(event, data) {
						actions.pushPagePath({instance:data.instance, path: ""}, {});

						var jm=json.breadcrumb.children.find(result => result.id === "manage-benefits");
						var jpu=jm.children.find(result => result.id === "partner-university");

						jpu.children.map(c => {
							c.selected = (c.data.instance==data.instance);
						});
						(window as any).intel.breadcrumbFromJSON.createFromJson(json);
						(window as any).intel.breadcrumbbar.initialize();
					};

					(window as any).intel.breadcrumbFromJSON.createFromJson(json);
					(window as any).intel.breadcrumbbar.initialize();
				}
			});
			
		}
	}

	public componentDidUpdate(prevProps: IPanelProps<IntelHeaderFooterConfig>) {
		if (this.props.locale !== prevProps.locale) {
			this.renderHeaderFooter();
		}
	}

	private renderHeaderFooter = () => {
		const { locale = 'en_US', config } = this.props;

		if (config.enableHeaderFooter) {
			const head = document.head;
			const body = document.body;
			const app = document.getElementById('app');

			// embed script
			let link = document.getElementById('intelHeaderFooter') as HTMLScriptElement;
			if (!link) {
				link = document.createElement("script");
				link.id = "intelHeaderFooter";
				link.onload = this.addHeaderSettings;
				link.type = "text/javascript";
				link.src = config.headerScriptLink?config.headerScriptLink: "https://www.intel.com/ighf/50recode.2/js/headerchooser.js";
				head.appendChild(link);
			} else {
				this.addHeaderSettings();
			}

			// header
			let header = document.getElementById('recode50header');
			if (!header) {
				header = document.createElement('div');
				header.id = 'recode50header';
				body.insertBefore(header, app);
			}

			// footer
			let footer = document.getElementById('recode50footer');
			if (!footer) {
				footer = document.createElement("div");
				footer.id = "recode50footer";
				body.append(footer);
			}
		}

		if (config.enableTmsCode) {
			// tms script
			window['wapSection'] = 'partner-university';
			window['wapSinglePage'] = true;

			const defaultSiteZone = config.defaultTimeZone ? config.defaultTimeZone : 'us-en'
			const localeParts = locale.replace('_', '-').toLowerCase().split('-');			
			window['wapLocalCode'] = localeParts.length > 1 ? localeParts[1] + '-' + localeParts[0] : defaultSiteZone ;

			let tms = document.getElementById('intelTms') as HTMLScriptElement;
			if (!tms) {
				const host = (window.document.location.protocol === 'http:') ? "http://www.intel.com" : "https://www.intel.com";
				const url = host + "/content/dam/www/global/wap/tms-loader.js"; // wap file url
				tms = document.createElement('script');
				tms.type = 'text/javascript';
				tms.async = true;
				tms.src = url;
				tms.id = 'intelTms';
				document.body.appendChild(tms);
			}
		}

	}

	private addHeaderSettings = () => {
		const { locale = "en_US" } = this.props;

		const INTELNAV = window['INTELNAV'] || {};

		INTELNAV.renderSettings = {
			version: "2.0 - 10/12/2017 08:00:00",
			textDirection: "ltr",
			culture: locale || "en_US",
			OutputId: "gh_default"
		};

		INTELNAV.renderSettingsFooter = {
			version: "2.0 - 10/12/2017 08:00:00",
			OutputId: "gf_default"
		};
	}

	private closeDialog = () => this.setState({modalOpen: false});
}



// WEBPACK FOOTER //
// ./src/panels/client/intelrco/IntelHeaderFooter.panel.tsx